import { CdkDragDrop } from '@angular/cdk/drag-drop';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import deepmerge from 'deepmerge';
import { GroupBase } from '../../../models/blueprint.model';
import {
  RecordUpdateEmission,
  ReportInfo,
  ReportOptions,
  ReportRecord,
  ReportView,
  TemplateProjectionMap,
} from '../../../models/report.model';
import { TemplateField } from '../../../models/template.model';
import {
  CONFIDENTIALITY_FIELD_ID,
  TOTAL_SIZE_FIELD_ID,
  NON_EDITABLE_FIELD_IDS,
} from '../../../shared/templates/config/common';
import { EditableFieldsService } from '../../editable-fields.service';
import { onDrop } from '../../summary-view/summary-view.helper';
import { entitiesWithConfidentiality } from '../../../shared/constants/entity.const';

@Component({
  selector: 'rep-table-info',
  templateUrl: './table-info.component.html',
  styleUrls: ['./table-info.component.scss'],
  standalone: false,
})
export class TableInfoComponent implements OnInit, OnChanges, OnDestroy {
  @Input() confidentialityTmpl: TemplateRef<any>;
  @Input() isPDF: boolean;
  @Input() reportData: ReportInfo;
  @Input() currentView: ReportView;
  @Input() groupsToDisplay: GroupBase<TemplateField>[];
  @Input() headerGroups: GroupBase<TemplateField>[];
  @Input() templateProjectionMap: TemplateProjectionMap;
  @Input() reportLocale: number;
  @Input() reportOptions: ReportOptions;
  @Output() reportUpdateClick: EventEmitter<RecordUpdateEmission> = new EventEmitter<RecordUpdateEmission>();
  @Output() removeRecord: EventEmitter<any> = new EventEmitter<any>();
  @Output() reportReordered: EventEmitter<ReportRecord[]> = new EventEmitter<ReportRecord[]>();

  reportRecordData: ReportRecord[];
  allFields: TemplateField[];
  headerFields: TemplateField[];
  totalSizeField: TemplateField;
  totalSize = '';
  confidentialityId = CONFIDENTIALITY_FIELD_ID;
  draggedRowIndex: number;
  nonEditableFieldIds = NON_EDITABLE_FIELD_IDS;
  isEntityWithConfidentiality: boolean;

  constructor(public editableFieldsService: EditableFieldsService) {}

  ngOnInit() {
    /* required by angular-l10n */
  }

  ngOnChanges({ currentView, reportData, groupsToDisplay, headerGroups }: SimpleChanges) {
    this.editableFieldsService.mapEditableFields(this.groupsToDisplay);
    if (
      (!!reportData && !!reportData.currentValue) ||
      (!!currentView && !!currentView.currentValue) ||
      (!!groupsToDisplay && !!groupsToDisplay.currentValue) ||
      (!!headerGroups && !!headerGroups.currentValue)
    ) {
      this.reportRecordData = deepmerge([], this.reportData ? this.reportData.records : null);
      this.allFields = [].concat(...(this.groupsToDisplay || []).map(group => group.fields));
      this.allFields.forEach(field => {
        field.label = field.label.replace('\\n', ' \n');
      });
      this.headerFields = []
        .concat(...(this.headerGroups || []).map(group => group.fields))
        .sort((a, b) => a.options.headerSortOrder - b.options.headerSortOrder);
      this.generateTotalSizeField();
      this.isEntityWithConfidentiality = !!entitiesWithConfidentiality[this.reportData.entity];
    }
  }

  ngOnDestroy(): void {
    /* required by angular-l10n */
  }

  emitEditableFields(event: MouseEvent, id: number, data: any) {
    const emittedObject = this.editableFieldsService.generateRecordUpdateEmission(event, id, data);
    if (emittedObject != null && !this.nonEditableFieldIds.includes(id)) {
      emittedObject.locale = this.reportLocale;
      this.reportUpdateClick.emit(emittedObject);
    }
  }

  generateTotalSizeField() {
    if (this.headerFields && this.headerFields.length > 0) {
      const totalSizeField = this.headerFields.find(a => a.id === TOTAL_SIZE_FIELD_ID[this.reportData.entity]);
      if (totalSizeField) {
        this.totalSizeField = totalSizeField;
      }
    }
  }

  dragStartColumn(startIndex: number) {
    this.draggedRowIndex = startIndex;
  }

  dropColumn(droppedIndex: number) {
    if (this.draggedRowIndex != droppedIndex) {
      onDrop(
        { previousIndex: this.draggedRowIndex, currentIndex: droppedIndex } as CdkDragDrop<
          Array<{ [key: string]: any }>
        >,
        this.reportData.records,
        this.reportReordered
      );
    }
  }
}
